import type { MockDatabase } from "@/types/domain";
import { seedMock } from "@/mocks/data/seed";
import { clearMockSession } from "@/mocks/session-state";

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Extended mock-backend side state (not part of bootstrap domain dump by default). */
export interface MockBackendExtras {
  secrets: Record<string, string>;
  systemSettings: {
    shortDomain: string;
    attributionWindowDays: number;
    cacheTtlSeconds: number;
  };
  experiments: Array<Record<string, unknown>>;
  activityLogs: Record<string, Array<Record<string, unknown>>>;
  packageHistory: Record<string, Array<Record<string, unknown>>>;
  specialCards: Array<Record<string, unknown>>;
  shareLanguages: unknown;
  regionsCountries: unknown;
  apiKeys: Record<string, Array<Record<string, unknown>>>;
  configSchemas: Array<Record<string, unknown>>;
}

export function createExtras(): MockBackendExtras {
  return {
    secrets: {},
    systemSettings: {
      shortDomain: "https://share.kqst.io",
      attributionWindowDays: 14,
      cacheTtlSeconds: 300,
    },
    experiments: [],
    activityLogs: {},
    packageHistory: {},
    specialCards: [],
    shareLanguages: { locales: ["en", "zh-Hans", "zh-Hant", "ja", "ko", "th"] },
    regionsCountries: { version: 1, groups: [] },
    apiKeys: {},
    configSchemas: [
      { name: "ShareContent", version: "v2.4" },
      { name: "LandingPageAsset", version: "v1.8" },
      { name: "ActivityConfig", version: "v2.1" },
      { name: "AttributionEvent", version: "v1.6" },
    ],
  };
}

/** In-memory mutable database used by mock HTTP handlers. */
export function createStore(seed: MockDatabase = seedMock as MockDatabase): MockDatabase {
  const db = deepClone(seed);
  db.GLOBAL_STATS.top_games = db.GAMES.filter((g) => g.status === "active");
  db.GLOBAL_STATS.channel_summary = db.CHANNEL_COMPARE;
  db.GLOBAL_STATS.total.games = db.GAMES.length;
  db.GLOBAL_STATS.total.packages = db.PACKAGES.length;
  if (!db.SHARE_SCENARIOS) {
    db.SHARE_SCENARIOS = [
      { id: "scn_invite", name: "好友邀请", desc: "好友召回、组队、邀请入坑等常规分享场景" },
      { id: "scn_recall", name: "流失召回", desc: "面向老玩家 / 流失玩家的回流激励场景" },
      { id: "scn_event", name: "活动分享", desc: "节日活动、限时礼包、首充福利等活动场景" },
    ];
  }
  return db;
}

export const store: MockDatabase = createStore();
export const extras: MockBackendExtras = createExtras();

export function recomputeGlobalStats() {
  store.GLOBAL_STATS.top_games = store.GAMES.filter((g) => g.status === "active");
  store.GLOBAL_STATS.channel_summary = store.CHANNEL_COMPARE;
  store.GLOBAL_STATS.total.games = store.GAMES.length;
  store.GLOBAL_STATS.total.packages = store.PACKAGES.length;
}

export function resetStore(): MockDatabase {
  clearMockSession();
  const next = createStore();
  for (const key of Object.keys(store) as Array<keyof MockDatabase>) {
    delete (store as unknown as Record<string, unknown>)[key as string];
  }
  Object.assign(store, next);
  const nextExtras = createExtras();
  for (const key of Object.keys(extras) as Array<keyof MockBackendExtras>) {
    delete (extras as unknown as Record<string, unknown>)[key as string];
  }
  Object.assign(extras, nextExtras);
  return store;
}

export function snapshotStore(): MockDatabase {
  return deepClone(store);
}

export function replaceStore(next: MockDatabase): MockDatabase {
  const cloned = deepClone(next);
  for (const key of Object.keys(store) as Array<keyof MockDatabase>) {
    delete (store as unknown as Record<string, unknown>)[key as string];
  }
  Object.assign(store, cloned);
  recomputeGlobalStats();
  return store;
}
